import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFileUpload } from "@features/file-upload/hooks/useFileUpload";
import { EmailRequestModal } from "@features/subscription/EmailRequestModal";
import { SubscriptionQuoteModal } from "@features/subscription/SubscriptionQuoteModal";
import { UpgradeLimitModal } from "@features/subscription/UpgradeLimitModal.tsx";
import { useSubscriptionCheckout } from "@features/subscription/useSubscriptionCheckout";
import {
  ALLOWED_FORMATS_TEXT,
  isFormatAllowed,
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_TEXT
} from "@shared/config/allowed-formats";
import { useAuth } from "@shared/contexts/auth-context";
import { toast } from "@shared/helper_lib/hooks/use-toast";
import { useBalance } from "@shared/hooks/useBalance";
import { useFileDuration } from "@shared/hooks/useFileDuration";
import { useFreePreview } from "@shared/hooks/useFreePreview";
import { useHistoryStore } from "@shared/store/history.store";
import { useUploadStore } from "@shared/store/upload.store";
import { Alert, AlertDescription } from "@shared/ui/components/alert";
import { Button } from "@shared/ui/components/button";
import { Card } from "@shared/ui/components/card";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, AlertTriangle, Clock, Sparkles, X } from "lucide-react";

import { FileSelect } from "./components/FileSelect";
import { UploadMethodSelector } from "./components/UploadMethodSelector";

export interface UploadSectionRef {
  openFileDialog: () => void;
}

// Функция для перевода ошибок валидации на русский
const translateValidationError = (errorMessage: string): string => {
  // Ошибки валидации URL
  if (
    errorMessage.includes("videoUrl must be a URL address") ||
    errorMessage.includes("videoUrl must be an URL address")
  ) {
    return "Некорректная ссылка на видео. Проверьте формат URL.";
  }
  if (errorMessage.includes("must be a URL")) {
    return "Некорректная ссылка. Введите корректный URL видео.";
  }

  // Ошибки yt-dlp
  if (errorMessage.includes("Video unavailable") || errorMessage.includes("ERROR: [youtube]")) {
    return "Видео недоступно. Проверьте ссылку или убедитесь, что видео не удалено и доступно для просмотра.";
  }
  if (errorMessage.includes("Private video") || errorMessage.includes("This video is private")) {
    return "Видео является приватным и недоступно для транскрибации.";
  }
  if (errorMessage.includes("Failed to process video URL")) {
    // Дополнительная обработка для других платформ
    if (errorMessage.includes("vk.com") || errorMessage.includes("vkvideo")) {
      return "Не удалось загрузить видео из VK. Проверьте настройки приватности видео.";
    }
    if (errorMessage.includes("rutube")) {
      return "Не удалось загрузить видео из Rutube. Проверьте ссылку и доступность видео.";
    }
    if (errorMessage.includes("ok.ru")) {
      return "Не удалось загрузить видео из Одноклассников. Проверьте ссылку и доступность видео.";
    }
    return "Не удалось обработать видео по ссылке. Проверьте корректность ссылки и доступность видео.";
  }

  return errorMessage;
};

export const UploadSection = forwardRef<
  UploadSectionRef,
  {
    onUploadComplete?: (
      fileIdOrUrl: number | string,
      fileName: string,
      language: string,
      model: string,
      estimatedMinutes?: number,
      detectLanguage?: boolean,
      preview?: boolean
    ) => void;
  }
>(({ onUploadComplete }, ref) => {
  const { isAuthenticated, openAuthModal, authModalOpen, user } = useAuth();
  const [isDragging, setIsDragging] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [model, setModel] = useState<"speed" | "quality">("quality");
  const [language, setLanguage] = useState<string>("ru");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [showYouTubeWarning, setShowYouTubeWarning] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);
  const validationErrorRef = useRef<HTMLDivElement>(null);
  const uploadErrorRef = useRef<HTMLDivElement>(null);
  const youtubeWarningRef = useRef<HTMLDivElement>(null);
  const pendingTranscribeRef = useRef(false);

  useImperativeHandle(ref, () => ({
    openFileDialog: () => {
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }));

  const { balance, refetch: refetchBalance } = useBalance();
  const { getDuration, isChecking } = useFileDuration();
  const [showCheckingScreen, setShowCheckingScreen] = useState(false);

  // Показываем экран "Определение длительности файла..." только если проверка
  // реально затянулась (>180мс) — иначе он лишь мигает между экранами
  useEffect(() => {
    if (!isChecking) {
      setShowCheckingScreen(false);
      return;
    }

    const timeoutId = setTimeout(() => setShowCheckingScreen(true), 180);
    return () => clearTimeout(timeoutId);
  }, [isChecking]);
  const { uploading, progress, error: uploadError, uploadFile, reset: resetUpload } = useFileUpload();
  const {
    activeUploads,
    currentFile,
    estimatedMinutes,
    videoUrl,
    uploadMethod,
    urlTranscriptionStarted,
    isLoadingMetadata,
    setCurrentFile,
    setEstimatedMinutes,
    setVideoUrl,
    setUploadMethod,
    setUrlTranscriptionStarted,
    setIsLoadingMetadata,
    clearAll,
    clearEstimatedMinutes,
    startUpload,
    completeUpload,
    setStatus
  } = useUploadStore();
  const { addOptimisticTranscription } = useHistoryStore();

  const { isPreviewMode, previewsLeft, maxFileSizeBytes, urlMaxMinutes, settingsLoaded } =
    useFreePreview(estimatedMinutes);
  const navigate = useNavigate();
  const { startCheckout, loadingPlan, emailModalProps, quoteModalProps } = useSubscriptionCheckout({
    onError: message => {
      toast({ title: "Ошибка", description: message, variant: "destructive" });
    }
  });

  useEffect(() => {
    if (isPreviewMode) {
      setModel("speed");
    }
  }, [isPreviewMode]);

  const activeMaxFileSize = isPreviewMode ? maxFileSizeBytes : MAX_FILE_SIZE;
  const activeMaxFileSizeText = isPreviewMode
    ? `${Math.round(maxFileSizeBytes / (1024 * 1024))} МБ`
    : MAX_FILE_SIZE_TEXT;
  const requiredMinutes = estimatedMinutes !== null ? estimatedMinutes : 10;
  const hasEnoughMinutes = isPreviewMode
    ? true
    : !isAuthenticated || !balance
      ? true
      : (balance.availableMinutes ?? balance.totalMinutes) >= requiredMinutes;
  const previewsExhausted = settingsLoaded && balance && previewsLeft === 0 && !hasEnoughMinutes;

  const file = currentFile;

  // Периодически проверяем активные загрузки на "зависание"
  useEffect(() => {
    const intervalId = setInterval(() => {
      const currentUploads = useUploadStore.getState().activeUploads;

      if (currentUploads.size === 0) {
        return;
      }

      const STALE_TIMEOUT = 60 * 1000; // 60 секунд без обновлений
      const uploads = Array.from(currentUploads.values());

      uploads.forEach(upload => {
        const timeSinceLastUpdate = Date.now() - upload.lastUpdateAt;
        // Не удаляем загрузки с uploadFinished=true, так как они показывают кнопку перехода в историю
        if (timeSinceLastUpdate > STALE_TIMEOUT && upload.status === "uploading" && !upload.uploadFinished) {
          const errorMessage =
            "Загрузка прервана из-за отсутствия обновлений. Проверьте интернет-соединение и попробуйте снова.";
          setValidationError(errorMessage);
          toast({
            variant: "destructive",
            title: "Ошибка загрузки",
            description: errorMessage
          });
          useUploadStore.getState().removeUpload(upload.uploadId);
          setCurrentFile(null);
          resetUpload();
        }
      });
    }, 5000); // Проверяем каждые 5 секунд (меньше нагрузка чем каждую секунду)

    return () => {
      clearInterval(intervalId);
    };
  }, [resetUpload]); // Убрали activeUploads из зависимостей - читаем напрямую из store

  const processFile = useCallback(
    async (file: File): Promise<void> => {
      // Проверяем только реально активные загрузки (исключая завершенные)
      const activeUploadsMap = useUploadStore.getState().activeUploads;
      const actuallyActiveUploads = Array.from(activeUploadsMap.values()).filter(
        upload => upload.status === "uploading" || upload.status === "merging"
      );

      if (actuallyActiveUploads.length > 0) {
        const errorMessage = "Дождитесь завершения текущей загрузки перед началом новой";
        setValidationError(errorMessage);
        toast({
          variant: "destructive",
          title: "Загрузка уже выполняется",
          description: errorMessage
        });
        return;
      }

      setValidationError(null);
      setEstimatedMinutes(null);

      // Очищаем ВСЕ завершенные загрузки перед началом новой
      const allUploads = Array.from(useUploadStore.getState().activeUploads.values());
      allUploads.forEach(upload => {
        // Удаляем завершенные и с ошибками
        if (upload.status === "completed" || upload.status === "error") {
          useUploadStore.getState().removeUpload(upload.uploadId);
        }
      });

      // Проверка размера файла (до проверки формата, т.к. это более критично)
      if (file.size > activeMaxFileSize) {
        const fileSizeGB = (file.size / 1024 / 1024 / 1024).toFixed(2);
        const fileSizeText = isPreviewMode ? `${Math.round(file.size / (1024 * 1024))} МБ` : `${fileSizeGB} ГБ`;
        const errorMessage = `Файл слишком большой. Максимальный размер: ${activeMaxFileSizeText}. Ваш файл: ${fileSizeText}`;
        setValidationError(errorMessage);
        toast({
          variant: "destructive",
          title: "Файл слишком большой",
          description: errorMessage
        });
        setCurrentFile(null);
        return;
      }

      if (!isFormatAllowed(file.type, file.name)) {
        const errorMessage = `Недопустимый тип файла: ${file.type}. Разрешенные форматы: ${ALLOWED_FORMATS_TEXT}`;
        setValidationError(errorMessage);
        toast({
          variant: "destructive",
          title: "Неподдерживаемый формат",
          description: errorMessage
        });
        setCurrentFile(null);
        return;
      }

      try {
        const duration = await getDuration(file);
        setEstimatedMinutes(duration);

        await refetchBalance();

        setCurrentFile(file);
        resetUpload();
      } catch (err) {
        console.warn("Не удалось определить длительность:", err);
        setCurrentFile(file);
        setEstimatedMinutes(null);
        resetUpload();
      }
    },
    [getDuration, refetchBalance, resetUpload, setCurrentFile, setEstimatedMinutes, activeMaxFileSize, isPreviewMode]
  );

  // Auto-scroll to YouTube warning when it appears (только если нет ошибок)
  useEffect(() => {
    if (showYouTubeWarning && youtubeWarningRef.current && !validationError && !uploadError) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          const element = youtubeWarningRef.current;
          if (element) {
            const elementRect = element.getBoundingClientRect();
            const absoluteElementTop = elementRect.top + window.pageYOffset;
            // Показываем в верхней части экрана с небольшим отступом (100px от верха)
            const targetPosition = absoluteElementTop - 100;
            window.scrollTo({ top: targetPosition, behavior: "smooth" });
          }
        }, 200);
      });
    }
  }, [showYouTubeWarning, validationError, uploadError]);

  // Auto-scroll to validation error when it appears (приоритет выше чем у warning)
  useEffect(() => {
    if (validationError && validationErrorRef.current) {
      // Используем requestAnimationFrame чтобы дождаться полного рендера
      requestAnimationFrame(() => {
        setTimeout(() => {
          // Прокручиваем к ошибке в верхнюю часть экрана с отступом (100px от верха)
          const element = validationErrorRef.current;
          if (element) {
            const elementRect = element.getBoundingClientRect();
            const absoluteElementTop = elementRect.top + window.pageYOffset;
            const targetPosition = absoluteElementTop - 100;
            window.scrollTo({ top: targetPosition, behavior: "smooth" });
          }
        }, 200);
      });
    }
  }, [validationError]);

  // Auto-scroll to upload error when it appears
  useEffect(() => {
    if (uploadError && uploadErrorRef.current) {
      // Показываем тост с ошибкой загрузки
      toast({
        variant: "destructive",
        title: "Ошибка загрузки файла",
        description: uploadError
      });

      // Используем requestAnimationFrame чтобы дождаться полного рендера
      requestAnimationFrame(() => {
        setTimeout(() => {
          // Прокручиваем к ошибке в верхнюю часть экрана с отступом (100px от верха)
          const element = uploadErrorRef.current;
          if (element) {
            const elementRect = element.getBoundingClientRect();
            const absoluteElementTop = elementRect.top + window.pageYOffset;
            const targetPosition = absoluteElementTop - 100;
            window.scrollTo({ top: targetPosition, behavior: "smooth" });
          }
        }, 200);
      });
    }
  }, [uploadError]);

  // Auto-scroll to file settings when file is selected (no errors)
  useEffect(() => {
    if (file && !validationError && !uploadError && !uploading && sectionRef.current) {
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 150);
    }
  }, [file, validationError, uploadError, uploading]);

  const handleDragOver = useCallback((e: React.DragEvent): void => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent): void => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent): void => {
      e.preventDefault();
      setIsDragging(false);

      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) {
        processFile(droppedFile);
      }
    },
    [processFile]
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      processFile(selectedFile);
    }
  };

  const handleRemoveFile = (): void => {
    // Очищаем весь стор (файл + все загрузки + URL состояние)
    clearAll();

    setValidationError(null);
    setIsTranscribing(false);
    setShowYouTubeWarning(false);
    resetUpload();

    // Очищаем файловый input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUrlContinue = useCallback(async () => {
    if (!videoUrl) {
      return;
    }

    setValidationError(null);
    setEstimatedMinutes(null);
    setShowYouTubeWarning(false);

    // Очищаем ВСЕ завершенные загрузки из activeUploads
    const allUploads = Array.from(useUploadStore.getState().activeUploads.values());
    allUploads.forEach(upload => {
      if (upload.status === "completed" || upload.status === "error") {
        useUploadStore.getState().removeUpload(upload.uploadId);
      }
    });

    // Очищаем пробелы и валидируем URL
    const trimmedUrl = videoUrl.trim();

    try {
      new URL(trimmedUrl);
    } catch {
      const errorMessage = "Некорректная ссылка. Введите корректный URL видео.";
      setValidationError(errorMessage);
      toast({
        variant: "destructive",
        title: "Некорректная ссылка",
        description: errorMessage
      });
      return;
    }

    // Проверяем поддерживаемые платформы
    const url = trimmedUrl.toLowerCase();
    const isSupported =
      // YouTube (desktop, mobile, shorts)
      url.includes("youtube.com") ||
      url.includes("youtu.be") ||
      url.includes("m.youtube.com") ||
      // VK (desktop, mobile)
      url.includes("vk.com") ||
      url.includes("m.vk.com") ||
      url.includes("vkvideo.ru") ||
      // Rutube (desktop, mobile)
      url.includes("rutube.ru") ||
      url.includes("m.rutube.ru") ||
      // OK.ru (desktop, mobile)
      url.includes("ok.ru") ||
      url.includes("m.ok.ru") ||
      // Дзен
      url.includes("dzen.ru") ||
      url.includes("zen.yandex.ru") ||
      // Google Drive
      url.includes("drive.google.com") ||
      url.includes("docs.google.com/file") ||
      // Яндекс.Диск
      url.includes("disk.yandex.") ||
      url.includes("yadi.sk") ||
      // Boosty (только открытые публикации)
      url.includes("boosty.to");

    if (!isSupported) {
      const errorMessage =
        "Поддерживаются только ссылки с YouTube, VK Video, Rutube, Одноклассники, Дзен, Google Drive, Яндекс.Диск и Boosty";
      setValidationError(errorMessage);
      toast({
        variant: "destructive",
        title: "Неподдерживаемая платформа",
        description: errorMessage
      });
      return;
    }

    // Обновляем состояние с очищенным URL
    setVideoUrl(trimmedUrl);

    // Создаем временный файл-заглушку сразу и переходим к экрану выбора модели
    try {
      const hostname = new URL(trimmedUrl).hostname;
      const urlFile = new File([], hostname, { type: "video/url" });
      setCurrentFile(urlFile);

      // Прокручиваем к блоку с файлом
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    } catch {
      setValidationError("Ошибка обработки URL");
      return;
    }

    // Начинаем загрузку метаданных в фоне
    setIsLoadingMetadata(true);
    setEstimatedMinutes(null);

    try {
      const { uploadApi } = await import("@shared/api/upload.api");
      const metadata = await uploadApi.getVideoMetadata(trimmedUrl);

      // Предупреждение для всех YouTube видео о нестабильности
      if (metadata.platform.toLowerCase().includes("youtube")) {
        setShowYouTubeWarning(true);
      }

      // В обычном режиме проверяем размер видео по ссылке
      if (!isPreviewMode && metadata.fileSize > activeMaxFileSize) {
        const fileSizeGB = (metadata.fileSize / 1024 / 1024 / 1024).toFixed(2);
        const fileSizeText = isPreviewMode ? `${Math.round(metadata.fileSize / (1024 * 1024))} МБ` : `${fileSizeGB} ГБ`;
        const errorMessage = `Видео слишком большое. Максимальный размер: ${activeMaxFileSizeText}. Размер видео: ${fileSizeText}`;
        setValidationError(errorMessage);
        toast({
          variant: "destructive",
          title: "Видео слишком большое",
          description: errorMessage
        });
        setCurrentFile(null);
        setIsLoadingMetadata(false);
        return;
      }

      // В режиме превью ограничиваем длительность видео
      if (isPreviewMode && metadata.estimatedMinutes > urlMaxMinutes) {
        const errorMessage = `Ролик слишком длинный для бесплатного превью. Максимальная длительность: ${urlMaxMinutes} мин`;
        setValidationError(errorMessage);
        toast({
          variant: "destructive",
          title: "Ролик слишком длинный",
          description: errorMessage
        });
        setCurrentFile(null);
        setIsLoadingMetadata(false);
        return;
      }

      // Устанавливаем расчетные минуты
      setEstimatedMinutes(metadata.estimatedMinutes);

      // Проверяем баланс
      await refetchBalance();

      // Обновляем имя файла на реальное название видео
      const urlFileWithTitle = new File([], metadata.title, { type: "video/url" });
      setCurrentFile(urlFileWithTitle);
      setIsLoadingMetadata(false);
    } catch (e: any) {
      const rawError = e.response?.data?.message || "Ошибка получения метаданных видео. Проверьте ссылку.";
      const errorMessage = translateValidationError(rawError);
      setValidationError(errorMessage);
      toast({
        variant: "destructive",
        title: "Ошибка загрузки метаданных",
        description: errorMessage
      });
      setCurrentFile(null);
      setIsLoadingMetadata(false);
    }
  }, [
    videoUrl,
    refetchBalance,
    setCurrentFile,
    setEstimatedMinutes,
    setIsLoadingMetadata,
    setVideoUrl,
    activeMaxFileSize,
    isPreviewMode,
    urlMaxMinutes
  ]);

  const handleTranscribe = async (): Promise<void> => {
    if (!isAuthenticated) {
      pendingTranscribeRef.current = true;
      openAuthModal("signup");
      return;
    }

    if (!file) {
      return;
    }

    if (user && user.transcriptionsUsed >= user.transcriptionsLimit) {
      setShowUpgradeModal(true);
      return;
    }

    // Проверяем, это URL или файл
    if (file.type === "video/url" && videoUrl) {
      // Создаем fake-upload для отображения модалки
      const uploadId = crypto.randomUUID();
      const fileSize = estimatedMinutes ? estimatedMinutes * 60 * 1024 * 1024 : 10485760; // Примерный размер

      // Запускаем "загрузку" со статусом uploading
      startUpload(uploadId, file.name, fileSize, 1);

      // Добавляем оптимистичную запись в историю для URL
      addOptimisticTranscription(videoUrl, file.name, estimatedMinutes || undefined, isPreviewMode, language);

      // Для URL просто вызываем коллбэк без загрузки
      if (onUploadComplete) {
        try {
          setIsTranscribing(true);
          setValidationError(null);
          // Передаем videoUrl как строку вместо fileId
          await onUploadComplete(
            videoUrl,
            file.name,
            language,
            model,
            estimatedMinutes || undefined,
            language === "auto",
            isPreviewMode
          );

          // Устанавливаем статус completed для модалки
          completeUpload(uploadId);

          // Устанавливаем флаг успешной отправки
          setUrlTranscriptionStarted(true);
          // Очищаем estimatedMinutes после успешной отправки
          clearEstimatedMinutes();
        } catch (error: any) {
          // Устанавливаем статус ошибки для модалки
          const rawError = error?.response?.data?.message || "Ошибка при создании транскрипции. Попробуйте снова.";
          const errorMessage = translateValidationError(rawError);
          setStatus(uploadId, "error", errorMessage);

          // Не показываем toast и Alert для 429 ошибок - они уже обработаны в interceptor
          if (error?.response?.status !== 429) {
            setValidationError(errorMessage);
            toast({
              variant: "destructive",
              title: "Ошибка транскрипции",
              description: errorMessage
            });
          } else {
            // Для 429 просто сбрасываем состояние без показа дополнительных ошибок
            setCurrentFile(null);
            setEstimatedMinutes(null);
            setVideoUrl("");
          }
        } finally {
          setIsTranscribing(false);
        }
      }
      return;
    }

    // Обычный файл - загружаем
    const fileId = await uploadFile(file);
    // uploadFinished устанавливается автоматически в useFileUpload.ts

    if (fileId) {
      // Добавляем оптимистичную запись в историю сразу после загрузки
      addOptimisticTranscription(fileId, file.name, estimatedMinutes || undefined, isPreviewMode, language);

      if (onUploadComplete) {
        try {
          setIsTranscribing(true);
          setValidationError(null);
          await onUploadComplete(
            fileId,
            file.name,
            language,
            model,
            estimatedMinutes || undefined,
            language === "auto",
            isPreviewMode
          );
          // Очищаем estimatedMinutes после успешной отправки
          clearEstimatedMinutes();
        } catch (error: any) {
          // Не показываем toast и Alert для 429 ошибок - они уже обработаны в interceptor
          if (error?.response?.status !== 429) {
            const rawError = error?.response?.data?.message || "Ошибка при создании транскрипции. Попробуйте снова.";
            const errorMessage = translateValidationError(rawError);
            setValidationError(errorMessage);
            toast({
              variant: "destructive",
              title: "Ошибка транскрипции",
              description: errorMessage
            });
          } else {
            // Для 429 просто сбрасываем состояние без показа дополнительных ошибок
            setCurrentFile(null);
            setEstimatedMinutes(null);
          }
        } finally {
          setIsTranscribing(false);
        }
      }
    } else {
      // Если загрузка не удалась, сбрасываем файл
      setCurrentFile(null);
      setEstimatedMinutes(null);
    }
  };

  // Продолжаем прерванный сценарий: аноним нажал «Начать транскрипцию», авторизовался -
  // и если минут достаточно, запускаем обработку автоматически, без повторного клика
  useEffect(() => {
    if (!pendingTranscribeRef.current || !isAuthenticated || !balance || !settingsLoaded) {
      return;
    }

    pendingTranscribeRef.current = false;

    const requiredMinutes = estimatedMinutes !== null ? estimatedMinutes : 10;
    const availableMinutes = balance.availableMinutes ?? balance.totalMinutes;

    if (availableMinutes >= requiredMinutes || isPreviewMode) {
      handleTranscribe();
    }
    // handleTranscribe намеренно не в зависимостях: пересоздаётся на каждый рендер
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, balance, estimatedMinutes, isPreviewMode, settingsLoaded]);

  // Модалку закрыли без авторизации - отложенный запуск отменяем
  useEffect(() => {
    if (!authModalOpen && !isAuthenticated) {
      pendingTranscribeRef.current = false;
    }
  }, [authModalOpen, isAuthenticated]);

  return (
    <>
      <section ref={sectionRef} className="pb-16 md:pb-24">
        <div className="container">
          <div className="mx-auto max-w-[680px] space-y-4">
            <AnimatePresence initial={false}>
              {showYouTubeWarning && (
                <motion.div
                  key="youtube-warning"
                  layout
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.18 }}
                  className="overflow-hidden"
                  ref={youtubeWarningRef}
                >
                  <Alert className="border-2 border-yellow-500 bg-yellow-50 dark:border-yellow-600 dark:bg-yellow-900/20 relative pr-12">
                    <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                    <AlertDescription className="text-yellow-900 dark:text-yellow-200">
                      <strong>Внимание:</strong>
                      Загрузка может быть нестабильной и прерваться в любой момент из-за ограничений со стороны YouTube.
                      Если возможно, используйте альтернативные платформы.
                    </AlertDescription>
                    <button
                      onClick={() => setShowYouTubeWarning(false)}
                      className="absolute right-2 top-2 rounded-md p-1 text-yellow-700 hover:text-yellow-900 dark:text-yellow-300 dark:hover:text-yellow-100 transition-colors"
                      aria-label="Закрыть предупреждение"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence initial={false}>
              {validationError && (
                <motion.div
                  key="validation-error"
                  layout
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.18 }}
                  className="overflow-hidden"
                  ref={validationErrorRef}
                >
                  <Alert variant="destructive" className="border-2 !bg-red-50">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{validationError}</AlertDescription>
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence initial={false}>
              {previewsExhausted && (
                <motion.div
                  key="previews-exhausted"
                  layout
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.18 }}
                  className="overflow-hidden"
                >
                  <Alert className="border-primary/30 bg-primary/5">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <AlertDescription>
                      <p className="font-medium text-foreground">Бесплатные превью закончились</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Оформите пробный период, чтобы получить полную расшифровку записи.
                      </p>
                      {/* Пробный тариф бэк отдаёт только новым: на платном плане
                          кнопка вела бы в ошибку — там ведём на тарифы */}
                      {balance?.plan === "free" ? (
                        <Button
                          size="sm"
                          className="mt-3"
                          disabled={loadingPlan !== null}
                          onClick={() => void startCheckout("trial")}
                        >
                          {loadingPlan === "trial" ? "Переходим к оплате..." : "Попробовать за 99 ₽"}
                        </Button>
                      ) : (
                        <Button size="sm" className="mt-3" onClick={() => navigate("/pricing")}>
                          Смотреть тарифы
                        </Button>
                      )}
                    </AlertDescription>
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence initial={false}>
              {uploadError && (
                <motion.div
                  key="upload-error"
                  layout
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.18 }}
                  className="overflow-hidden"
                  ref={uploadErrorRef}
                >
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{uploadError}</AlertDescription>
                  </Alert>
                </motion.div>
              )}
            </AnimatePresence>

            <Card
              className={`overflow-hidden p-6 md:p-8 transition-colors ${isDragging ? "border-primary bg-primary/5" : "border-border"}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <AnimatePresence mode="wait" initial={false}>
                {showCheckingScreen ? (
                  <motion.div
                    key="checking"
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="flex flex-col items-center justify-center gap-3 py-10 text-muted-foreground"
                  >
                    <Clock className="h-6 w-6 animate-spin" />
                    Определение длительности файла...
                  </motion.div>
                ) : !file ? (
                  <motion.div
                    key="method"
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <UploadMethodSelector
                      uploadMethod={uploadMethod}
                      setUploadMethod={setUploadMethod}
                      videoUrl={videoUrl}
                      setVideoUrl={setVideoUrl}
                      onFileSelect={handleFileSelect}
                      onUrlContinue={handleUrlContinue}
                      fileInputRef={fileInputRef}
                      maxFileSizeText={activeMaxFileSizeText}
                    />
                  </motion.div>
                ) : (
                  (() => {
                    const uploadsWithFinished = Array.from(activeUploads.values()).filter(u => u.uploadFinished);
                    const hasUploadFinished = uploadsWithFinished.length > 0;
                    const uploadFinishedValue = hasUploadFinished || urlTranscriptionStarted || false;

                    return (
                      <motion.div
                        key="file"
                        layout
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                      >
                        <FileSelect
                          file={file}
                          onRemoveFile={handleRemoveFile}
                          onTranscribe={handleTranscribe}
                          model={model}
                          setModel={setModel}
                          language={language}
                          setLanguage={setLanguage}
                          isAuthenticated={isAuthenticated}
                          uploading={uploading || activeUploads.size > 0}
                          progress={
                            activeUploads.size > 0
                              ? Math.round(
                                  (Array.from(activeUploads.values())[0].uploadedChunks /
                                    Array.from(activeUploads.values())[0].totalChunks) *
                                    100
                                )
                              : progress
                          }
                          uploadFinished={uploadFinishedValue}
                          estimatedMinutes={estimatedMinutes}
                          isFileTooLarge={file.size > activeMaxFileSize}
                          maxFileSizeText={activeMaxFileSizeText}
                          isLoadingMetadata={isLoadingMetadata}
                          isTranscribing={isTranscribing}
                          isPreviewMode={isPreviewMode}
                        />
                      </motion.div>
                    );
                  })()
                )}
              </AnimatePresence>
            </Card>
          </div>
        </div>
      </section>

      <UpgradeLimitModal open={showUpgradeModal} onOpenChange={setShowUpgradeModal} />
      <EmailRequestModal {...emailModalProps} />
      <SubscriptionQuoteModal {...quoteModalProps} />
    </>
  );
});

UploadSection.displayName = "UploadSection";
